<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import ButtonMoreOptions from '../buttons/buttonMoreOptions.vue';
import { _mft } from '../../i18n.js';
import {
    browserStorage,
    planPresetFromQuery,
    takePlanPreset,
} from '../../utils/planPreset.mjs';

const props = defineProps({
    // Shown to companies without an active plan: they can only buy one, so
    // no plan counts as current and subscription management is hidden.
    paywall: {
        type: Boolean,
        default: false,
    },
});

const PLAN_ORDER = ['basic', 'essentials', 'pro'];
const CADENCE_ORDER = ['monthly', 'yearly', 'lifetime'];
const ACTIVATION_POLL_INTERVAL_MS = 3000;
const ACTIVATION_POLL_ATTEMPTS = 20;

const isLoading = ref(true);
const isAwaitingActivation = ref(false);
const activationTimedOut = ref(false);
const showConfetti = ref(false);
const subscription = ref({
    plan: 'basic',
    status: 'inactive',
    cadence: null,
    access_until: null,
    grace_until: null,
    cancel_at: null,
    canceled_at: null,
    is_canceling: false,
    needs_payment_method: false,
    is_free_plan: true,
});
const plans = ref([]);
const showFaq = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);
const selectedCadence = reactive({});
const needsPaymentMethod = ref(false);
const setupUrl = ref(null);
const isCancelling = ref(false);
const isResuming = ref(false);
const isOpeningPortal = ref(false);
const activeCheckoutKey = ref(null);
const hasStripeCustomer = ref(false);
const selectedCurrency = ref('EUR');
const availableCurrencies = ref(['EUR']);
const currencyPlans = ref([]);

const featureMatrix = {
    basic: [
        _mft('myflowPro:plans.basic.features.coreCrm'),
        _mft('myflowPro:plans.basic.features.websiteBuilder'),
        _mft('myflowPro:plans.basic.features.chatSupport'),
    ],
    essentials: [
        _mft('myflowPro:plans.essentials.features.everythingInBasic'),
        _mft('myflowPro:plans.essentials.features.unlimitedCrm'),
        _mft('myflowPro:plans.essentials.features.emailJourneys'),
        _mft('myflowPro:plans.essentials.features.domainWebsite'),
    ],
    pro: [
        _mft('myflowPro:plans.pro.features.everythingInEssentials'),
        _mft('myflowPro:plans.pro.features.contracts'),
        _mft('myflowPro:plans.pro.features.leadHunter'),
        _mft('myflowPro:plans.pro.features.webinars'),
    ],
};

const comparisonFeatures = [
    {
        category: _mft('myflowPro:comparison.aiTeam.category'),
        features: [
            {
                name: _mft('myflowPro:comparison.aiTeam.teammates'),
                basic: '1',
                essentials: '3',
                pro: _mft('myflowPro:comparison.aiTeam.allEight'),
                highlight: true,
            },
            {
                name: _mft('myflowPro:comparison.aiTeam.monthlyCapacity'),
                basic: _mft('myflowPro:comparison.aiTeam.standard'),
                essentials: '2.5x',
                pro: '10x',
                highlight: true,
            },
        ],
    },
    {
        category: _mft('myflowPro:comparison.emailOutreach.category'),
        features: [
            {
                name: _mft('myflowPro:comparison.emailOutreach.emailsIncluded'),
                basic: '100',
                essentials: '250',
                pro: '1,000',
            },
        ],
    },
    {
        category: _mft('myflowPro:comparison.support.category'),
        features: [
            {
                name: _mft('myflowPro:comparison.support.chat'),
                basic: true,
                essentials: true,
                pro: true,
            },
            {
                name: _mft('myflowPro:comparison.support.priority'),
                basic: false,
                essentials: false,
                pro: true,
            },
        ],
    },
    {
        category: _mft('myflowPro:comparison.more.category'),
        features: [
            {
                name: _mft('myflowPro:comparison.more.adminSeats'),
                basic: 1,
                essentials: 3,
                pro: 10,
            },
            {
                name: _mft('myflowPro:comparison.more.ownApp'),
                basic: false,
                essentials: false,
                pro: true,
            },
        ],
    },
];

const disclaimers = [
    _mft('myflowPro:disclaimers.emailLimits'),
    _mft('myflowPro:disclaimers.adminAccounts'),
    _mft('myflowPro:disclaimers.appStoreRelease'),
];

const planRank = (plan) => {
    const index = PLAN_ORDER.indexOf(plan ?? 'basic');
    return index === -1 ? 0 : index;
};

// The plan the company holds. On the paywall there is none: an inactive
// company still reads as Basic, which must not show up as its plan.
const currentPlan = computed(() =>
    props.paywall ? null : subscription.value.plan
);
const currentPlanRank = computed(() =>
    planRank(subscription.value.plan ?? 'basic')
);
const shouldHighlightEssentials = computed(
    () => props.paywall || subscription.value.plan === 'basic'
);

// The free tier is where a company starts: on Basic, never billed by Stripe.
// A churned customer also lands back on Basic with is_free_plan true, but their
// status reads canceled or expired, so they keep the plain plan name.
const isFreeTier = computed(
    () =>
        subscription.value.is_free_plan &&
        subscription.value.plan === 'basic' &&
        !['canceled', 'expired'].includes(subscription.value.status)
);

const planBadgeLabel = computed(() =>
    isFreeTier.value
        ? _mft('myflowPro:plans.basic.freeHeadline')
        : planHeadline(subscription.value.plan)
);

const statusCopy = computed(() => {
    const status = subscription.value.status;
    if (status === 'active') {
        const until = subscription.value.access_until;
        const cancelOn = subscription.value.is_canceling
            ? subscription.value.cancel_at
            : null;
        if (until && cancelOn) {
            return _mft('myflowPro:status.activeUntilCancels', {
                date: formatIsoDate(until),
                cancelDate: formatIsoDate(cancelOn),
            });
        }

        return until
            ? _mft('myflowPro:status.activeUntil', {
                  date: formatIsoDate(until),
              })
            : _mft('myflowPro:status.active');
    }

    if (status === 'past_due') {
        const grace = subscription.value.grace_until;
        return grace
            ? _mft('myflowPro:status.pastDueAccessEnds', {
                  date: formatIsoDate(grace),
              })
            : _mft('myflowPro:status.pastDue');
    }

    if (status === 'canceled') {
        return _mft('myflowPro:status.canceled');
    }

    return _mft('myflowPro:status.noActiveSubscription');
});

const showCancellationCta = computed(() => {
    return (
        subscription.value.is_free_plan === false &&
        subscription.value.status !== 'canceled' &&
        !subscription.value.is_canceling
    );
});

const cancellationCopy = computed(() => {
    if (!subscription.value.is_canceling) {
        return null;
    }

    if (subscription.value.cancel_at) {
        return _mft('myflowPro:cancellation.endsOn', {
            date: formatIsoDate(subscription.value.cancel_at),
        });
    }

    return _mft('myflowPro:cancellation.endsAtPeriodClose');
});

function buildGroups(options) {
    const map = new Map();

    options.forEach((option) => {
        const key = option.plan;
        if (!map.has(key)) {
            map.set(key, {
                plan: key,
                planGroup: option.plan_group,
                displayName: option.display_name,
                description: option.description,
                options: [],
            });
        }

        map.get(key).options.push(option);
    });

    const groups = Array.from(map.values()).sort(
        (a, b) => planRank(a.plan) - planRank(b.plan)
    );

    groups.forEach((group) => {
        group.options.sort((a, b) => {
            const indexA = CADENCE_ORDER.indexOf(a.cadence ?? 'monthly');
            const indexB = CADENCE_ORDER.indexOf(b.cadence ?? 'monthly');
            return indexA - indexB;
        });
    });

    return groups;
}

const groupedPlans = computed(() => buildGroups(plans.value));

watch(
    groupedPlans,
    (groups) => {
        groups.forEach((group) => {
            if (!group.options.length) {
                return;
            }

            const currentCadence =
                group.plan === currentPlan.value
                    ? subscription.value.cadence
                    : null;

            if (
                currentCadence &&
                group.options.some((opt) => opt.cadence === currentCadence)
            ) {
                selectedCadence[group.plan] = currentCadence;
                return;
            }

            if (!selectedCadence[group.plan]) {
                selectedCadence[group.plan] = group.options[0].cadence;
            }
        });
    },
    { immediate: true }
);

watch(
    () => [currentPlan.value, subscription.value.cadence],
    () => {
        const group = groupedPlans.value.find(
            (item) => item.plan === currentPlan.value
        );
        if (group) {
            const match = group.options.find(
                (opt) => opt.cadence === subscription.value.cadence
            );
            if (match) {
                selectedCadence[group.plan] = match.cadence;
            }
        }
    }
);

function formatCurrency(amountCents, currency = 'eur') {
    return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency?.toUpperCase() ?? 'EUR',
        minimumFractionDigits: 0,
    }).format(amountCents / 100);
}

function formatIsoDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function matchingCurrencyPlan(option) {
    const planId = option.plan_group ?? option.plan;

    return currencyPlans.value.find((plan) => plan.id === planId);
}

function displayCurrency(option) {
    const currency = selectedCurrency.value?.toUpperCase();
    const cadence = option.cadence ?? 'monthly';
    const matchingPlan = matchingCurrencyPlan(option);
    const hasConvertedPrice = matchingPlan?.[cadence]?.[currency] !== undefined;

    return hasConvertedPrice
        ? currency
        : (option.currency ?? currency ?? 'EUR').toUpperCase();
}

function displayAmountCents(option) {
    const currency = displayCurrency(option);
    const cadence = option.cadence ?? 'monthly';
    const matchingPlan = matchingCurrencyPlan(option);
    const convertedAmount = matchingPlan?.[cadence]?.[currency];

    if (typeof convertedAmount === 'number') {
        return Math.round(convertedAmount * 100);
    }

    return option.amount_cents ?? 0;
}

function formatPrice(option) {
    const amountCents = displayAmountCents(option);
    const currency = displayCurrency(option);
    const formatted = formatCurrency(amountCents, currency);

    if (option.cadence === 'monthly') {
        return _mft('myflowPro:price.monthly', { price: formatted });
    }

    if (option.cadence === 'yearly') {
        return _mft('myflowPro:price.yearly', { price: formatted });
    }

    return formatted;
}

function pricingMeta(option) {
    const original = option.original_amount_cents ?? null;
    const current = displayAmountCents(option);
    const currency = displayCurrency(option);

    // Scale the original price proportionally to the selected currency
    const scaleFactor =
        (option.amount_cents ?? 0) > 0 ? current / option.amount_cents : 1;
    const scaledOriginal = original ? Math.round(original * scaleFactor) : null;

    const savings =
        scaledOriginal && scaledOriginal > current
            ? scaledOriginal - current
            : null;

    return {
        originalFormatted: scaledOriginal
            ? formatCurrency(scaledOriginal, currency)
            : null,
        savingsFormatted: savings ? formatCurrency(savings, currency) : null,
        savingsAmount: savings,
    };
}

function yearlySavings(option, group) {
    if (option.cadence !== 'yearly') {
        return null;
    }

    const monthlyOption = group.options.find(
        (opt) => opt.cadence === 'monthly'
    );
    if (!monthlyOption) {
        return null;
    }

    const baseMonthly = displayAmountCents(monthlyOption);
    const yearlyFull = baseMonthly * 12;
    const savingsCents = yearlyFull - displayAmountCents(option);
    const currency = displayCurrency(option);

    if (savingsCents <= 0) {
        return null;
    }

    const months = savingsCents / (baseMonthly || 1);

    return {
        amountFormatted: formatCurrency(savingsCents, currency),
        monthsSaved: Math.round(months),
    };
}

function getSelectedOption(group) {
    return (
        group.options.find(
            (opt) => opt.cadence === selectedCadence[group.plan]
        ) ?? group.options[0]
    );
}

function checkoutKey(group, option) {
    return `${group.plan}-${option?.cadence ?? 'monthly'}`;
}

function loadSubscription() {
    isLoading.value = true;
    return axios
        .get(route('billing.subscription.current'))
        .then(({ data }) => {
            subscription.value = {
                plan: data.plan ?? 'basic',
                status: data.status ?? 'inactive',
                cadence: data.cadence ?? null,
                access_until: data.access_until ?? null,
                grace_until: data.grace_until ?? null,
                cancel_at: data.cancel_at ?? null,
                canceled_at: data.canceled_at ?? null,
                is_canceling: data.is_canceling ?? false,
                needs_payment_method: data.needs_payment_method ?? false,
                is_free_plan: data.is_free_plan ?? true,
            };
            plans.value = data.available_plans ?? [];
            needsPaymentMethod.value = data.needs_payment_method ?? false;
            hasStripeCustomer.value = data.has_stripe_customer ?? false;
            setupUrl.value = null;
        })
        .catch((error) => {
            console.error(error);
            errorMessage.value = _mft('myflowPro:errors.loadBillingStatus');
        })
        .finally(() => {
            isLoading.value = false;
        });
}

function loadCurrencies() {
    axios
        .get('/api/pricing/plans')
        .then(({ data }) => {
            availableCurrencies.value = data.currencies ?? ['EUR'];
            currencyPlans.value = data.plans ?? [];

            if (
                data.default_currency &&
                availableCurrencies.value.includes(data.default_currency)
            ) {
                selectedCurrency.value = data.default_currency;
            }
        })
        .catch((error) => {
            console.error(_mft('myflowPro:errors.loadCurrencyPricing'), error);
        });
}

function startCheckout(option) {
    if (!option || option.is_free) {
        errorMessage.value = _mft('myflowPro:errors.contactSupportDowngrade');
        return;
    }

    const nextCheckoutKey = `${option.plan}-${option.cadence ?? 'monthly'}`;
    if (activeCheckoutKey.value === nextCheckoutKey) {
        return;
    }

    activeCheckoutKey.value = nextCheckoutKey;

    return axios
        .post(route('billing.subscription.checkout'), {
            plan: option.plan,
            cadence: option.cadence,
            ...(props.paywall
                ? {
                      success_url: `${route('billing.plan-required')}?checkout=success`,
                      cancel_url: `${route('billing.plan-required')}?checkout=cancelled`,
                  }
                : {}),
        })
        .then(({ data }) => {
            if (data?.status === 'support_notified') {
                errorMessage.value = _mft(
                    'myflowPro:errors.supportNotifiedPlanChange'
                );
                activeCheckoutKey.value = null;
                return;
            }

            if (data?.url) {
                window.open(data.url, '_self', 'noopener');
                return;
            }

            activeCheckoutKey.value = null;
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value =
                apiMessage ?? _mft('myflowPro:errors.startCheckout');
            activeCheckoutKey.value = null;
        });
}

function requestCardSetup() {
    axios
        .post(route('billing.subscription.cardSetup'))
        .then(({ data }) => {
            if (data?.url) {
                setupUrl.value = data.url;
                window.open(data.url, '_blank', 'noopener');
            }
        })
        .catch((error) => {
            console.error(error);
            errorMessage.value = _mft('myflowPro:errors.startCardSetup');
        });
}

function openBillingPortal() {
    if (isOpeningPortal.value) {
        return;
    }

    isOpeningPortal.value = true;
    errorMessage.value = null;

    axios
        .post(route('billing.subscription.portal'))
        .then(({ data }) => {
            if (data?.url) {
                window.open(data.url, '_self', 'noopener');
            }
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value =
                apiMessage ?? _mft('myflowPro:errors.openBillingPortal');
        })
        .finally(() => {
            isOpeningPortal.value = false;
        });
}

function cancelSubscription() {
    if (isCancelling.value) {
        return;
    }

    isCancelling.value = true;
    errorMessage.value = null;

    axios
        .delete(route('billing.subscription.cancel'))
        .then(({ data }) => {
            const status = data?.status ?? null;
            const cancelAt = data?.cancel_at ?? subscription.value.cancel_at;
            const formattedDate = cancelAt ? formatIsoDate(cancelAt) : null;

            if (status === 'support_notified') {
                successMessage.value = _mft(
                    'myflowPro:cancellation.supportNotified'
                );
            } else if (status === 'already_scheduled') {
                successMessage.value = formattedDate
                    ? _mft('myflowPro:cancellation.alreadyScheduledUntil', {
                          date: formattedDate,
                      })
                    : _mft('myflowPro:cancellation.alreadyScheduledPeriodEnd');
            } else if (status === 'scheduled') {
                successMessage.value = formattedDate
                    ? _mft('myflowPro:cancellation.scheduledUntil', {
                          date: formattedDate,
                      })
                    : _mft('myflowPro:cancellation.scheduledPeriodEnd');
            } else {
                successMessage.value = _mft(
                    'myflowPro:cancellation.processing'
                );
            }

            return loadSubscription();
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value =
                apiMessage ?? _mft('myflowPro:errors.cancelSubscription');
        })
        .finally(() => {
            isCancelling.value = false;
        });
}

function resumeSubscription() {
    if (isResuming.value) {
        return;
    }

    isResuming.value = true;
    errorMessage.value = null;
    successMessage.value = null;

    axios
        .post(route('billing.subscription.resume'))
        .then(({ data }) => {
            const status = data?.status ?? null;

            if (status === 'support_notified') {
                successMessage.value = _mft(
                    'myflowPro:cancellation.resumeSupportNotified'
                );
            } else {
                successMessage.value = _mft('myflowPro:cancellation.resumed');
            }

            return loadSubscription();
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value =
                apiMessage ?? _mft('myflowPro:errors.resumeSubscription');
        })
        .finally(() => {
            isResuming.value = false;
        });
}

function setCadence(planCode, cadence) {
    selectedCadence[planCode] = cadence;
}

function isCurrentOption(group, option) {
    return (
        group.plan === currentPlan.value &&
        option.cadence === subscription.value.cadence
    );
}

function planDisplayName(group) {
    if (group.displayName) {
        return group.displayName;
    }

    return planHeadline(group.plan);
}

function planHeadline(planCode) {
    switch (planCode) {
        case 'essentials':
            return 'Essentials';
        case 'pro':
            return 'Pro';
        default:
            return 'Basic';
    }
}

function cadenceLabel(option) {
    if (option.cadence === 'yearly') {
        return _mft('myflowPro:cadence.yearlyWithSavings');
    }

    if (option.cadence === 'monthly') {
        return _mft('myflowPro:cadence.monthly');
    }

    return _mft('myflowPro:cadence.oneTime');
}

function ctaLabel(group, option) {
    if (props.paywall) {
        return _mft('myflowPro:actions.getPlan', {
            plan: planDisplayName(group),
        });
    }

    if (isCurrentOption(group, option)) {
        return _mft('myflowPro:actions.currentPlan');
    }

    if (group.plan === currentPlan.value) {
        return option.cadence === 'yearly'
            ? _mft('myflowPro:actions.switchToYearly')
            : _mft('myflowPro:actions.switchToMonthly');
    }

    const targetRank = planRank(group.plan);
    if (targetRank > currentPlanRank.value) {
        return _mft('myflowPro:actions.upgradeToPlan', {
            plan: planDisplayName(group),
        });
    }

    if (targetRank < currentPlanRank.value) {
        return _mft('myflowPro:actions.downgradeToPlan', {
            plan: planDisplayName(group),
        });
    }

    return _mft('myflowPro:actions.switchToPlan', {
        plan: planDisplayName(group),
    });
}

function isCtaDisabled(group, option) {
    if (activeCheckoutKey.value === checkoutKey(group, option)) {
        return true;
    }

    if (option.is_free) {
        return group.plan === currentPlan.value;
    }

    return isCurrentOption(group, option);
}

function handleAction(group) {
    const option = getSelectedOption(group);
    startCheckout(option);
}

const tierBadgeClass = computed(() => {
    const status = subscription.value.status;
    if (status === 'active') {
        return 'bg-myflowLavender text-black border border-myflowPurple w-full text-left lg:text-center lg:justify-center py-4';
    }
    if (status === 'past_due') {
        return 'bg-red-200 text-black border border-red-500';
    }
    return 'bg-gray-100 text-gray-700 border border-gray-200';
});

// Stripe sends the buyer back before its webhook has activated the plan, so
// wait for the subscription to turn active and then let the paywall route
// decide where to go: it forwards companies with access to the dashboard.
function awaitActivation(attempt = 1) {
    axios
        .get(route('billing.subscription.current'))
        .then(({ data }) => {
            if (['active', 'trialing'].includes(data.status)) {
                window.location.assign(route('billing.plan-required'));
                return;
            }

            scheduleActivationCheck(attempt);
        })
        .catch(() => scheduleActivationCheck(attempt));
}

function scheduleActivationCheck(attempt) {
    if (attempt >= ACTIVATION_POLL_ATTEMPTS) {
        activationTimedOut.value = true;
        return;
    }

    setTimeout(() => awaitActivation(attempt + 1), ACTIVATION_POLL_INTERVAL_MS);
}

onMounted(() => {
    loadCurrencies();

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    let planPreset = null;
    if (props.paywall && checkoutStatus === null) {
        const savedPreset = takePlanPreset(browserStorage());
        planPreset = planPresetFromQuery(window.location.search) ?? savedPreset;
    }

    if (props.paywall && checkoutStatus === 'success') {
        isAwaitingActivation.value = true;
        awaitActivation();
    } else if (planPreset) {
        startCheckout({ ...planPreset, is_free: false })?.finally(() => {
            if (activeCheckoutKey.value === null) {
                loadSubscription();
            }
        });
    } else if (checkoutStatus === 'success') {
        document.getElementById('upgrade-top-banner')?.remove();
        isLoading.value = true;
        setTimeout(() => {
            loadSubscription();
            isLoading.value = false;
            showConfetti.value = true;
        }, 5000);
    } else if (checkoutStatus === 'portal-success') {
        successMessage.value = _mft('myflowPro:success.billingDetailsUpdated');
        setTimeout(() => {
            loadSubscription();
            successMessage.value = null;
        }, 5000);
    } else {
        loadSubscription();
    }
});
</script>

<template>
    <div class="w-full">
        <div
            v-if="isAwaitingActivation"
            class="max-w-xl px-4 py-12 mx-auto text-center"
        >
            <h1 class="text-3xl font-semibold text-slate-900">
                {{ _mft('myflowPro:paywall.activation.title') }}
            </h1>
            <template v-if="!activationTimedOut">
                <p class="mt-4 text-slate-600">
                    {{ _mft('myflowPro:paywall.activation.waiting') }}
                </p>
                <mf-spinner-medium class="w-12 h-6 mx-auto mt-6"></mf-spinner-medium>
            </template>
            <p v-else class="mt-4 text-slate-600">
                {{ _mft('myflowPro:paywall.activation.timedOut') }}
                <a :href="route('billing.plan-required')" class="mf-link">{{
                    _mft('myflowPro:paywall.activation.tryAgain')
                }}</a>
            </p>
        </div>

        <div v-else class="max-w-5xl px-4 py-12 mx-auto">
            <div class="mb-4 text-center">
                <h1 class="text-3xl font-semibold text-slate-900">
                    {{
                        paywall
                            ? _mft('myflowPro:paywall.title')
                            : _mft('myflowPro:hero.title')
                    }}
                </h1>
                <p v-if="paywall" class="max-w-2xl mx-auto mt-4 text-slate-600">
                    {{ _mft('myflowPro:paywall.description') }}
                </p>

                <div
                    v-if="!paywall"
                    class="flex space-x-4 items-center justify-center mt-4"
                >
                    <div
                        v-if="!isLoading"
                        class="w-full inline-flex flex-col md:flex-row items-start md:items-center md:justify-center md:gap-2 px-8 md:px-4 py-4 rounded-wl relative"
                        :class="tierBadgeClass"
                    >
                        <strong class="tracking-wide uppercase">{{
                            planBadgeLabel
                        }}</strong>
                        <span
                            class="text-gray-400 hidden md:inline"
                            v-if="subscription.status === 'active'"
                            >•</span
                        >
                        <span>{{ statusCopy }}</span>

                        <span
                            class="absolute right-6 md:right-2 top-0 bottom-0 flex items-center h-full"
                        >
                            <mf-spinner-medium
                                v-if="isOpeningPortal"
                                class="w-12 h-6"
                            ></mf-spinner-medium>
                            <button-more-options
                                v-if="!isOpeningPortal && hasStripeCustomer"
                                :label="_mft('myflowPro:actions.manage')"
                                style-type="secondary"
                                alignment="right"
                            >
                                <button-more-options-option
                                    v-if="hasStripeCustomer"
                                    class="whitespace-nowrap"
                                    @click="openBillingPortal"
                                >
                                    {{
                                        _mft(
                                            'myflowPro:actions.billingAndReceipts'
                                        )
                                    }}
                                </button-more-options-option>

                                <button-more-options-option-with-confirmation
                                    v-if="showCancellationCta"
                                    style-type="danger"
                                    class="whitespace-nowrap"
                                    :confirm-text="
                                        _mft('shared:confirm.areYouSure')
                                    "
                                    @confirmed="cancelSubscription"
                                >
                                    {{
                                        _mft(
                                            'myflowPro:actions.cancelSubscription'
                                        )
                                    }}
                                </button-more-options-option-with-confirmation>
                            </button-more-options>
                        </span>
                    </div>
                </div>
            </div>

            <div
                v-if="!paywall && (isCancelling || subscription.is_canceling)"
                class="mx-auto mt-6"
            >
                <div
                    class="flex flex-col gap-4 px-6 py-5 border rounded-2xl border-slate-200 bg-slate-50 md:flex-row md:items-center md:justify-between"
                >
                    <div class="space-y-1 text-sm text-slate-700">
                        <p v-if="cancellationCopy" class="mb-0">{{ cancellationCopy }}</p>
                        <p
                            v-else
                            class="mb-0"
                            v-html="_mft('myflowPro:cancellation.prompt')"
                        ></p>
                        <p
                            v-if="subscription.is_canceling"
                            class="mb-0 text-xs text-slate-500"
                        >
                            <span
                                v-html="
                                    _mft('myflowPro:cancellation.resumeHint')
                                "
                            ></span>
                        </p>
                    </div>
                    <button
                        v-if="subscription.is_canceling"
                        type="button"
                        class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition border rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        :class="
                            isResuming
                                ? 'border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed focus-visible:ring-slate-200'
                                : 'border-myflowGreen-900 bg-myflowGreen-900 text-white hover:bg-myflowGreen-800 focus-visible:ring-myflowGreen-900'
                        "
                        :disabled="isResuming"
                        @click="resumeSubscription"
                    >
                        <span v-if="isResuming">{{
                            _mft('myflowPro:actions.resuming')
                        }}</span>
                        <span v-else>{{
                            _mft('myflowPro:actions.resumeSubscription')
                        }}</span>
                    </button>
                    <button
                        v-else
                        type="button"
                        class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition border rounded-lg border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed focus-visible:outline-none"
                        disabled
                    >
                        <span>{{ _mft('myflowPro:actions.cancelling') }}</span>
                    </button>
                </div>
            </div>

            <div v-if="errorMessage" class="max-w-3xl mx-auto mb-8">
                <div
                    class="px-4 py-3 text-red-800 border border-red-200 rounded bg-red-50"
                >
                    {{ errorMessage }}
                </div>
            </div>

            <div v-if="successMessage" class="max-w-3xl mx-auto mb-8 text-center">
                <div
                    class="w-full px-4 py-3 text-green-800 border border-green-200 rounded bg-green-50 text-center"
                >
                    {{ successMessage }}
                </div>
            </div>

            <!-- Currency switcher -->
            <div
                v-if="availableCurrencies.length > 1"
                class="flex justify-center mb-4"
            >
                <div
                    class="inline-flex items-stretch gap-1 p-1 mt-6 border rounded-full border-slate-200 bg-slate-50"
                >
                    <button
                        v-for="currency in availableCurrencies"
                        :key="currency"
                        type="button"
                        class="px-4 py-1.5 text-xs font-semibold transition rounded-full"
                        :class="
                            currency === selectedCurrency
                                ? 'bg-myflowGreen-900 text-white shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                        "
                        @click="selectedCurrency = currency"
                    >
                        {{ currency }}
                    </button>
                </div>
            </div>

            <div v-if="isLoading" class="flex justify-center py-20">
                <div
                    class="inline-flex flex-col items-center justify-center px-4 py-2 text-sm border rounded-lg text-slate-600 border-slate-200"
                >
                    <mf-spinner-medium
                        class="w-12 h-6 mx-auto"
                    ></mf-spinner-medium>
                    <div class="pt-2">
                        {{ _mft('myflowPro:loading.billingInformation') }}
                    </div>
                </div>
            </div>

            <div v-else class="space-y-8">
                <div class="grid gap-6 lg:grid-cols-3">
                    <card-basic
                        v-for="group in groupedPlans"
                        :key="group.plan"
                        class="flex flex-col h-full"
                        :class="{
                            'border-myflowGreen-900 shadow-myflowGreen-100 ring-2 ring-myflowGreen-100':
                                group.plan === currentPlan,
                        }"
                    >
                        <div class="space-y-4 mb-6">
                            <div class="flex items-center justify-between">
                                <h2
                                    class="text-xl font-semibold text-slate-900"
                                >
                                    {{ planDisplayName(group) }}
                                </h2>
                                <span
                                    v-if="group.plan === currentPlan"
                                    class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase rounded-full bg-myflowGreen-100 text-myflowGreen-900"
                                >
                                    {{ _mft('myflowPro:plans.yourPlan') }}
                                </span>
                            </div>

                            <div
                                v-if="group.options.length > 1"
                                class="flex items-stretch gap-2 p-1 border rounded-full border-slate-200 bg-slate-50"
                            >
                                <button
                                    v-for="option in group.options"
                                    :key="`${group.plan}-${option.cadence}`"
                                    type="button"
                                    class="flex-1 px-4 py-2 text-xs font-semibold transition rounded-full whitespace-nowrap"
                                    :class="
                                        option.cadence ===
                                        selectedCadence[group.plan]
                                            ? 'bg-myflowGreen-900 text-white shadow-sm'
                                            : 'text-slate-600'
                                    "
                                    @click="
                                        setCadence(group.plan, option.cadence)
                                    "
                                >
                                    <span v-html="cadenceLabel(option)"></span>
                                </button>
                            </div>

                            <div
                                v-for="option in [getSelectedOption(group)]"
                                :key="`${group.plan}-${option.cadence}-price`"
                                class="space-y-0"
                            >
                                <template
                                    v-for="meta in [pricingMeta(option)]"
                                    :key="`${group.plan}-${option.cadence}-meta`"
                                >
                                    <p
                                        v-if="
                                            meta.originalFormatted &&
                                            option.amount_cents !==
                                                option.original_amount_cents
                                        "
                                        class="flex items-center gap-2 text-sm text-slate-500"
                                    >
                                        <span>{{
                                            _mft('myflowPro:price.usually')
                                        }}</span>
                                        <span class="line-through">
                                            {{ meta.originalFormatted }}
                                        </span>
                                        <span
                                            v-if="meta.savingsAmount"
                                            class="uppercase text-xs bg-myflowGreen-100 text-myflowGreen-900 px-2 py-0.5 rounded-full"
                                        >
                                            {{
                                                _mft(
                                                    'myflowPro:price.saveAmount',
                                                    {
                                                        amount: meta.savingsFormatted,
                                                    }
                                                )
                                            }}
                                        </span>
                                    </p>
                                    <p
                                        class="text-2xl font-semibold text-slate-900 text-center !mb-0"
                                    >
                                        {{ formatPrice(option) }}
                                    </p>
                                    <p
                                        v-if="
                                            option.cadence === 'monthly' ||
                                            option.cadence === 'yearly'
                                        "
                                        class="text-xs text-slate-500 text-center"
                                    >
                                        {{
                                            _mft('myflowPro:price.excludingVat')
                                        }}
                                    </p>
                                    <p
                                        v-if="option.cadence === 'monthly'"
                                        class="text-xs text-slate-500 text-center pb-2"
                                    >
                                        {{
                                            _mft(
                                                'myflowPro:price.billedMonthly'
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="option.cadence === 'yearly'"
                                        class="text-xs text-slate-500 text-center pb-2"
                                    >
                                        {{
                                            _mft('myflowPro:price.billedYearly')
                                        }}
                                    </p>
                                    <p
                                        v-if="yearlySavings(option, group)"
                                        class="mt-2 text-xs text-myflowGreen-900 text-center"
                                    >
                                        {{
                                            _mft(
                                                'myflowPro:price.yearlySavings',
                                                {
                                                    amount: yearlySavings(
                                                        option,
                                                        group
                                                    ).amountFormatted,
                                                    count: yearlySavings(
                                                        option,
                                                        group
                                                    ).monthsSaved,
                                                }
                                            )
                                        }}
                                    </p>
                                </template>
                            </div>
                        </div>

                        <!--                        <ul class="flex-1 px-6 py-6 space-y-3 text-sm text-slate-700">-->
                        <!--                            <li-->
                        <!--                                v-for="feature in featureMatrix[group.plan] ?? []"-->
                        <!--                                :key="feature"-->
                        <!--                                class="flex items-start gap-3"-->
                        <!--                            >-->
                        <!--                                <svg class="w-4 h-4 mt-1 text-myflowGreen-900 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">-->
                        <!--                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z" clip-rule="evenodd" />-->
                        <!--                                </svg>-->
                        <!--                                <span>{{ feature }}</span>-->
                        <!--                            </li>-->
                        <!--                        </ul>-->

                        <div class="px-6 pb-6 mt-auto">
                            <p
                                v-if="
                                    getSelectedOption(group).is_free &&
                                    group.plan !== currentPlan
                                "
                                class="mb-3 text-xs text-slate-500"
                            >
                                {{
                                    _mft('myflowPro:plans.basic.downgradeHelp')
                                }}
                            </p>
                            <button
                                type="button"
                                class="inline-flex items-center justify-center w-full gap-2 px-5 py-3 text-sm font-semibold transition rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                :class="
                                    isCtaDisabled(
                                        group,
                                        getSelectedOption(group)
                                    )
                                        ? 'border border-myflowGreen-900 text-myflowGreen-900 bg-myflowGreen-100 focus-visible:ring-myflowGreen-900 cursor-not-allowed'
                                        : 'border border-myflowGreen-900 text-white bg-myflowGreen-900 hover:bg-myflowGreen-800 focus-visible:ring-myflowGreen-900'
                                "
                                :disabled="
                                    isCtaDisabled(
                                        group,
                                        getSelectedOption(group)
                                    )
                                "
                                @click="handleAction(group)"
                            >
                                {{ ctaLabel(group, getSelectedOption(group)) }}
                            </button>
                            <mf-spinner-medium
                                v-if="
                                    activeCheckoutKey ===
                                    checkoutKey(group, getSelectedOption(group))
                                "
                                class="w-12 h-6 mx-auto mt-3"
                            ></mf-spinner-medium>
                        </div>
                    </card-basic>
                </div>
            </div>

            <!-- Feature comparison table -->
            <div v-if="!isLoading" class="mt-12">
                <div class="text-center mb-4">
                    <h2 class="text-2xl font-bold text-slate-900">
                        {{ _mft('myflowPro:comparison.title') }}
                    </h2>
                </div>

                <div class="overflow-x-scroll w-full">
                    <card-basic class="overflow-hidden" :padless="true">
                        <table class="w-full text-sm">
                            <!-- Header -->
                            <thead>
                                <tr class="bg-slate-50">
                                    <th class="px-6 py-6 text-left w-2/5"></th>
                                    <th class="px-5 pb-6 pt-8 text-center">
                                        <span
                                            class="block text-lg font-bold text-slate-800"
                                            >Basic</span
                                        >
                                        <span
                                            class="block text-xs text-slate-400 font-normal mt-0.5"
                                            >{{
                                                _mft(
                                                    'myflowPro:plans.basic.tagline'
                                                )
                                            }}</span
                                        >
                                    </th>
                                    <th
                                        class="px-5 pb-6 pt-8 text-center relative"
                                        :class="
                                            shouldHighlightEssentials
                                                ? 'bg-myflowGreen-50 border-x-2 border-t-2 border-myflowGreen-400'
                                                : ''
                                        "
                                    >
                                        <span
                                            v-if="shouldHighlightEssentials"
                                            class="inline-flex items-center gap-1.5 px-3 py-0.5 text-xxs font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-myflowGreen-700 to-myflowGreen-900 text-white shadow-md mb-2 absolute top-1 left-1/2 -translate-x-1/2 whitespace-nowrap"
                                        >
                                            <svg
                                                class="w-3 h-3"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                                                />
                                            </svg>
                                            {{
                                                _mft(
                                                    'myflowPro:plans.essentials.mostPopular'
                                                )
                                            }}
                                        </span>
                                        <span
                                            class="block text-lg font-bold"
                                            :class="
                                                shouldHighlightEssentials
                                                    ? 'text-myflowGreen-900'
                                                    : 'text-slate-800'
                                            "
                                            >Essentials</span
                                        >
                                        <span
                                            class="block text-xs font-normal mt-0.5"
                                            :class="
                                                shouldHighlightEssentials
                                                    ? 'text-myflowGreen-700/70'
                                                    : 'text-slate-400'
                                            "
                                            >{{
                                                _mft(
                                                    'myflowPro:plans.essentials.tagline'
                                                )
                                            }}</span
                                        >
                                    </th>
                                    <th class="px-5 pb-6 pt-8 text-center">
                                        <span
                                            class="block text-lg font-bold text-slate-800"
                                            >Pro</span
                                        >
                                        <span
                                            class="block text-xs text-slate-400 font-normal mt-0.5"
                                            >{{
                                                _mft(
                                                    'myflowPro:plans.pro.tagline'
                                                )
                                            }}</span
                                        >
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <template
                                    v-for="(section, si) in comparisonFeatures"
                                    :key="si"
                                >
                                    <!-- "Best for" section renders as cards -->
                                    <template
                                        v-if="section.features[0]?.isExample"
                                    >
                                        <tr>
                                            <td class="px-6 py-4 align-top">
                                                <span
                                                    class="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-slate-400"
                                                    >{{
                                                        section.category
                                                    }}</span
                                                >
                                            </td>
                                            <td
                                                class="px-3 py-4 text-center align-top"
                                            >
                                                <div
                                                    class="rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 px-4 py-4"
                                                >
                                                    <span
                                                        class="block text-xs text-slate-500 leading-relaxed"
                                                        >{{
                                                            section.features[0]
                                                                .basic
                                                        }}</span
                                                    >
                                                </div>
                                            </td>
                                            <td
                                                class="px-3 py-4 text-center align-top"
                                                :class="
                                                    shouldHighlightEssentials
                                                        ? 'bg-myflowGreen-50/40 border-x-2 border-myflowGreen-400'
                                                        : ''
                                                "
                                            >
                                                <div
                                                    class="rounded-xl px-4 py-4 shadow-sm"
                                                    :class="
                                                        shouldHighlightEssentials
                                                            ? 'bg-gradient-to-br from-myflowGreen-50 to-myflowGreen-100 border border-myflowGreen-300'
                                                            : 'bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200'
                                                    "
                                                >
                                                    <span
                                                        class="block text-xs leading-relaxed"
                                                        :class="
                                                            shouldHighlightEssentials
                                                                ? 'text-myflowGreen-800 font-medium'
                                                                : 'text-slate-500'
                                                        "
                                                        >{{
                                                            section.features[0]
                                                                .essentials
                                                        }}</span
                                                    >
                                                </div>
                                            </td>
                                            <td
                                                class="px-3 py-4 text-center align-top"
                                            >
                                                <div
                                                    class="rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 px-4 py-4 shadow-sm"
                                                >
                                                    <span
                                                        class="block text-xs text-slate-200 leading-relaxed"
                                                        >{{
                                                            section.features[0]
                                                                .pro
                                                        }}</span
                                                    >
                                                </div>
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Normal feature sections -->
                                    <template v-else>
                                        <tr>
                                            <td class="px-6 pt-5 pb-1">
                                                <span
                                                    class="text-xs font-bold uppercase tracking-wider text-slate-400"
                                                    >{{
                                                        section.category
                                                    }}</span
                                                >
                                            </td>
                                            <td></td>
                                            <td
                                                :class="
                                                    shouldHighlightEssentials
                                                        ? 'bg-myflowGreen-50/40 border-x-2 border-myflowGreen-400'
                                                        : ''
                                                "
                                            ></td>
                                            <td></td>
                                        </tr>
                                        <tr
                                            v-for="(
                                                feat, fi
                                            ) in section.features"
                                            :key="`${si}-${fi}`"
                                            class="border-t border-slate-100 transition-colors group"
                                        >
                                            <td
                                                class="px-6 py-4 text-slate-600 group-hover:text-slate-900 transition-colors"
                                                :class="
                                                    feat.highlight
                                                        ? 'font-medium text-slate-700'
                                                        : ''
                                                "
                                            >
                                                {{ feat.name }}
                                            </td>
                                            <td
                                                class="px-5 py-4 text-center group-hover:bg-slate-50/60 transition-colors"
                                            >
                                                <template
                                                    v-if="feat.basic === true"
                                                >
                                                    <svg
                                                        class="w-5 h-5 mx-auto text-myflowGreen-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                                                            clip-rule="evenodd"
                                                        />
                                                    </svg>
                                                </template>
                                                <template
                                                    v-else-if="
                                                        feat.basic === false
                                                    "
                                                >
                                                    <span class="text-slate-300"
                                                        >—</span
                                                    >
                                                </template>
                                                <template v-else>
                                                    <span
                                                        class="font-semibold text-slate-600"
                                                        :class="
                                                            feat.highlight
                                                                ? 'text-base'
                                                                : ''
                                                        "
                                                        >{{ feat.basic }}</span
                                                    >
                                                </template>
                                            </td>
                                            <td
                                                class="px-5 py-4 text-center transition-colors"
                                                :class="
                                                    shouldHighlightEssentials
                                                        ? 'bg-myflowGreen-50/40 border-x-2 border-myflowGreen-400 group-hover:bg-myflowGreen-50/70'
                                                        : 'group-hover:bg-slate-50/60'
                                                "
                                            >
                                                <template
                                                    v-if="
                                                        feat.essentials === true
                                                    "
                                                >
                                                    <svg
                                                        class="w-5 h-5 mx-auto text-myflowGreen-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                                                            clip-rule="evenodd"
                                                        />
                                                    </svg>
                                                </template>
                                                <template
                                                    v-else-if="
                                                        feat.essentials ===
                                                        false
                                                    "
                                                >
                                                    <span class="text-slate-300"
                                                        >—</span
                                                    >
                                                </template>
                                                <template v-else>
                                                    <span
                                                        :class="[
                                                            shouldHighlightEssentials
                                                                ? 'font-bold text-myflowGreen-900'
                                                                : 'font-semibold text-slate-600',
                                                            feat.highlight
                                                                ? shouldHighlightEssentials
                                                                    ? 'text-lg'
                                                                    : 'text-base'
                                                                : shouldHighlightEssentials
                                                                ? 'text-base'
                                                                : '',
                                                        ]"
                                                        >{{
                                                            feat.essentials
                                                        }}</span
                                                    >
                                                </template>
                                            </td>
                                            <td
                                                class="px-5 py-4 text-center group-hover:bg-slate-50/60 transition-colors"
                                            >
                                                <template
                                                    v-if="feat.pro === true"
                                                >
                                                    <svg
                                                        class="w-5 h-5 mx-auto text-myflowGreen-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                                                            clip-rule="evenodd"
                                                        />
                                                    </svg>
                                                </template>
                                                <template
                                                    v-else-if="
                                                        feat.pro === false
                                                    "
                                                >
                                                    <span class="text-slate-300"
                                                        >—</span
                                                    >
                                                </template>
                                                <template v-else>
                                                    <span
                                                        class="font-bold text-slate-800"
                                                        :class="
                                                            feat.highlight
                                                                ? 'text-lg'
                                                                : 'text-base'
                                                        "
                                                        >{{ feat.pro }}</span
                                                    >
                                                </template>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                                <tr
                                    class="bg-slate-50 border-t border-slate-100 transition-colors group"
                                >
                                    <td
                                        class="px-6 py-4 text-slate-600 group-hover:text-slate-900 transition-colors"
                                    ></td>

                                    <td
                                        v-for="group in groupedPlans"
                                        :key="group.plan"
                                        class="px-4 py-7 text-center"
                                        :class="
                                            group.plan === 'essentials' &&
                                            shouldHighlightEssentials
                                                ? 'bg-myflowGreen-50/40 border-x-2 border-b-2 border-myflowGreen-400'
                                                : ''
                                        "
                                    >
                                        <button
                                            type="button"
                                            class="inline-flex items-center justify-center w-full gap-2 px-5 py-3 text-sm font-bold transition-all rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2"
                                            :class="
                                                group.plan === currentPlan
                                                    ? 'border-2 border-myflowGreen-300 text-myflowGreen-700 bg-myflowGreen-50 cursor-not-allowed'
                                                    : group.plan ===
                                                      'essentials'
                                                    ? 'text-white bg-gradient-to-r from-myflowGreen-700 to-myflowGreen-900 hover:from-myflowGreen-600 hover:to-myflowGreen-800 focus:ring-myflowGreen-500'
                                                    : group.plan === 'pro'
                                                    ? 'text-white bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-600 hover:to-slate-800 focus:ring-slate-500'
                                                    : 'border-2 border-slate-300 text-slate-700 bg-white hover:border-slate-400 hover:bg-slate-50 focus:ring-slate-400'
                                            "
                                            :disabled="
                                                group.plan === currentPlan
                                            "
                                            @click="handleAction(group)"
                                        >
                                            {{
                                                group.plan === currentPlan
                                                    ? _mft(
                                                          'myflowPro:actions.currentPlan'
                                                      )
                                                    : _mft(
                                                          'myflowPro:actions.getPlan',
                                                          {
                                                              plan: planDisplayName(
                                                                  group
                                                              ),
                                                          }
                                                      )
                                            }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Bottom CTA row -->
                        <div
                            class="hidden grid grid-cols-4 border-t border-slate-200 bg-slate-50"
                        >
                            <div class="px-6 py-7"></div>
                            <div
                                v-for="group in groupedPlans"
                                :key="group.plan"
                                class="px-4 py-7 text-center"
                                :class="
                                    group.plan === 'essentials' &&
                                    shouldHighlightEssentials
                                        ? 'bg-myflowGreen-50/40 border-x-2 border-b-2 border-myflowGreen-400'
                                        : ''
                                "
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center justify-center w-full gap-2 px-5 py-3 text-sm font-bold transition-all rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    :class="
                                        group.plan === currentPlan
                                            ? 'border-2 border-myflowGreen-300 text-myflowGreen-700 bg-myflowGreen-50 cursor-not-allowed'
                                            : group.plan === 'essentials'
                                            ? 'text-white bg-gradient-to-r from-myflowGreen-700 to-myflowGreen-900 hover:from-myflowGreen-600 hover:to-myflowGreen-800 shadow-lg shadow-myflowGreen-900/25 hover:shadow-xl hover:-translate-y-0.5 focus:ring-myflowGreen-500'
                                            : group.plan === 'pro'
                                            ? 'text-white bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-600 hover:to-slate-800 shadow-md hover:shadow-lg hover:-translate-y-0.5 focus:ring-slate-500'
                                            : 'border-2 border-slate-300 text-slate-700 bg-white hover:border-slate-400 hover:bg-slate-50 hover:-translate-y-0.5 focus:ring-slate-400'
                                    "
                                    :disabled="group.plan === currentPlan"
                                    @click="handleAction(group)"
                                >
                                    {{
                                        group.plan === currentPlan
                                            ? _mft(
                                                  'myflowPro:actions.currentPlan'
                                              )
                                            : _mft(
                                                  'myflowPro:actions.getPlan',
                                                  {
                                                      plan: planDisplayName(
                                                          group
                                                      ),
                                                  }
                                              )
                                    }}
                                </button>
                            </div>
                        </div>

                        <div class="py-4 md:py-6 lg:py-8">
                            <ul class="text-center text-xs text-gray-400">
                                <li v-for="(disclaimer, key) in disclaimers">
                                    {{ disclaimer }}
                                </li>
                            </ul>
                        </div>
                    </card-basic>
                </div>
            </div>

            <company-myflow-billing-card-setup
                v-if="needsPaymentMethod && !paywall"
                :url="setupUrl"
                @request-setup="requestCardSetup"
            />

            <p v-if="paywall" class="mt-10 text-sm text-center text-slate-500">
                <a :href="route('user.logout')" class="mf-link">{{
                    _mft('myflowPro:paywall.logout')
                }}</a>
            </p>
        </div>

        <confetti-bomb v-if="showConfetti" />
    </div>
</template>
